import { type Money, ZERO, money, sum, sub } from "../money/money";
import { isCashTender } from "./finalize";

/**
 * Tender accumulation and change.
 *
 * The last computation before money physically moves, and the one a customer
 * verifies in real time while standing at the counter. Change being wrong is
 * the most visible failure a till can have.
 */

export class TenderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TenderError";
  }
}

export interface TenderEntry {
  id: string;
  type: string;
  amount: Money;
  reference: string | null;
}

export interface TenderState {
  due: Money;
  tendered: Money;
  /** Still owed. Zero once settled. */
  remaining: Money;
  change: Money;
  settled: boolean;
  cashTendered: Money;
}

export function tenderState(due: Money, tenders: readonly TenderEntry[]): TenderState {
  for (const tender of tenders) {
    if (tender.amount <= 0) {
      throw new TenderError(
        `Tender ${tender.id}: amount must be greater than zero, got ${tender.amount}`,
      );
    }
  }

  const tendered = sum(tenders.map((t) => t.amount));
  const cashTendered = sum(tenders.filter((t) => isCashTender(t.type)).map((t) => t.amount));

  const settled = tendered >= due;
  const overpayment = settled ? sub(tendered, due) : ZERO;

  // Change leaves the drawer, so it can only be funded by cash that entered it.
  // Allowing otherwise would both misstate the drawer and let the terminal be
  // used to turn e-wallet balance into cash.
  if (overpayment > cashTendered) {
    throw new TenderError(
      `Overpayment of ${overpayment} exceeds cash tendered ${cashTendered}; ` +
        `non-cash tenders cannot produce change`,
    );
  }

  return {
    due,
    tendered,
    remaining: settled ? ZERO : money(due - tendered),
    change: overpayment,
    settled,
    cashTendered,
  };
}

/**
 * Quick-cash buttons.
 *
 * These remove arithmetic from a staff member mid-rush. The exact amount comes
 * first because it is by far the most common case; the rest are the notes
 * someone is realistically holding. Deliberately short — a wall of buttons
 * takes longer to scan than a keypad takes to type.
 */
const NOTE_DENOMINATIONS = [2000, 5000, 10_000, 20_000, 50_000, 100_000];
const MAX_OPTIONS = 5;

export function quickCashOptions(due: Money): number[] {
  if (due <= 0) return [];

  const options: number[] = [due];

  // Round up to the next whole note of increasing size.
  for (const denomination of NOTE_DENOMINATIONS) {
    const rounded = Math.ceil(due / denomination) * denomination;
    if (rounded >= due) options.push(rounded);
  }

  // Plus the next few plain notes above the amount due.
  for (const note of NOTE_DENOMINATIONS) {
    if (note >= due) options.push(note);
  }

  const unique = [...new Set(options)].sort((a, b) => a - b);
  return unique.slice(0, MAX_OPTIONS);
}
